import { useState, useEffect, FC } from 'react';
import { Ticket, Sparkles, AlertCircle } from 'lucide-react';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { fetchCoupons, Coupon, ApplyCouponResult } from '../../api/coupons';

interface ApplyCouponDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  targetType: 'offer' | 'queue_item';
  targetId: string;
  targetTitle: string;
  currentPrice?: number | null;
  originalPrice?: number | null;
  marketplace?: string | null;
  onApplied: (result: ApplyCouponResult) => void;
  applyFn: (
    token: string,
    id: string,
    payload: { coupon_id?: string; coupon_code?: string }
  ) => Promise<ApplyCouponResult>;
}

export const ApplyCouponDrawer: FC<ApplyCouponDrawerProps> = ({
  isOpen,
  onClose,
  targetType,
  targetId,
  targetTitle,
  currentPrice,
  originalPrice,
  marketplace,
  onApplied,
  applyFn,
}) => {
  const { token } = useAuth();
  const { success, error: showError } = useToast();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [manualCode, setManualCode] = useState('');

  useEffect(() => {
    if (!isOpen || !token) return;
    setIsLoading(true);
    fetchCoupons(token, { is_active: true })
      .then((data) => {
        setCoupons(data);
      })
      .catch((err) => {
        showError(err.message || 'Erro ao carregar catálogo de cupons.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [isOpen, token, showError]);

  const handleApply = async (couponId?: string, couponCode?: string) => {
    if (!token || !targetId) return;
    setIsApplying(true);
    try {
      const res = await applyFn(token, targetId, {
        coupon_id: couponId,
        coupon_code: couponCode,
      });
      success(
        res.message || 'Cupom aplicado com sucesso e copy atualizada!',
        'Cupom Vinculado'
      );
      onApplied(res);
      onClose();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Falha ao aplicar cupom.';
      showError(msg, 'Erro na Aplicação');
    } finally {
      setIsApplying(false);
    }
  };

  const basePrice = currentPrice || originalPrice || 0;

  const calculateSimulation = (coupon: Coupon) => {
    if (basePrice <= 0) return null;
    let discount = 0;
    if (coupon.discount_type === 'percent') {
      discount = (basePrice * coupon.discount_value) / 100;
    } else {
      discount = Math.min(basePrice, coupon.discount_value);
    }
    const finalPrice = Math.max(0, basePrice - discount);
    return {
      discount,
      finalPrice,
    };
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Aplicar Cupom com 1 Clique"
      description={
        targetType === 'offer'
          ? 'Selecione um cupom para vincular à oferta'
          : 'Selecione um cupom para vincular ao item da fila'
      }
    >
      <div className="space-y-5">
        {/* Card do Produto/Item Alvo */}
        <div className="rounded-xl border border-surface-border bg-surface-subtle p-3.5">
          <div className="flex items-center justify-between">
            <p className="text-2xs font-semibold uppercase tracking-wider text-content-muted">
              {targetType === 'offer' ? 'Oferta Selecionada' : 'Item da Fila'}
            </p>
            {marketplace && (
              <Badge variant="neutral">{marketplace.toUpperCase()}</Badge>
            )}
          </div>
          <p className="mt-1 line-clamp-2 text-xs font-bold text-content-main">
            {targetTitle}
          </p>
          {basePrice > 0 && (
            <p className="mt-1 text-xs text-content-muted">
              Preço Base Atual:{' '}
              <span className="font-semibold text-content-main">
                {basePrice.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </span>
            </p>
          )}
        </div>

        {/* Inserção Manual Rápida */}
        <div className="rounded-xl border border-surface-border bg-surface-panel p-3.5">
          <p className="text-2xs font-bold uppercase tracking-wider text-content-muted">
            Digitar Código de Cupom
          </p>
          <div className="mt-2 flex gap-2">
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
              placeholder="Ex: 10OFF, VALE20"
              leftIcon={<Ticket className="h-4 w-4" />}
            />
            <Button
              variant="secondary"
              disabled={!manualCode.trim() || isApplying}
              isLoading={isApplying}
              onClick={() => handleApply(undefined, manualCode.trim())}
            >
              Aplicar
            </Button>
          </div>
        </div>

        {/* Lista de Cupons do Catálogo */}
        <div>
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-content-muted">
              Cupons Ativos no Catálogo ({coupons.length})
            </h4>
          </div>

          {isLoading ? (
            <div className="mt-3 space-y-2.5">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-20 animate-pulse rounded-xl bg-surface-subtle"
                />
              ))}
            </div>
          ) : coupons.length === 0 ? (
            <div className="mt-3 rounded-xl border border-dashed border-surface-border p-6 text-center">
              <Ticket className="mx-auto h-8 w-8 text-content-muted" />
              <p className="mt-2 text-xs font-semibold text-content-main">
                Nenhum cupom ativo no catálogo
              </p>
              <p className="mt-1 text-2xs text-content-muted">
                Cadastre cupons na aba Catálogo de Cupons para aplicação rápida.
              </p>
            </div>
          ) : (
            <div className="mt-3 space-y-2.5">
              {coupons.map((coupon) => {
                const sim = calculateSimulation(coupon);
                const isMinOrderBlocked =
                  Boolean(coupon.min_order_value) &&
                  basePrice > 0 &&
                  basePrice < (coupon.min_order_value || 0);

                return (
                  <div
                    key={coupon.id}
                    className="flex flex-col gap-2 rounded-xl border border-surface-border bg-surface-panel p-3.5 transition-colors hover:border-brand-500/40"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-lg border border-brand-500/20 bg-brand-500/10 px-2 py-0.5 font-mono text-xs font-extrabold text-brand-600 dark:text-brand-400">
                          <Ticket className="h-3.5 w-3.5" />
                          {coupon.code}
                        </span>
                        <Badge variant="neutral">
                          {coupon.discount_type === 'percent'
                            ? `${coupon.discount_value}% OFF`
                            : `R$ ${coupon.discount_value.toFixed(2)}`}
                        </Badge>
                      </div>

                      <Button
                        size="sm"
                        variant="primary"
                        disabled={isApplying || isMinOrderBlocked}
                        onClick={() => handleApply(coupon.id)}
                        leftIcon={<Sparkles className="h-3.5 w-3.5" />}
                      >
                        Aplicar
                      </Button>
                    </div>

                    {/* Simulação de Preço Final */}
                    {sim && !isMinOrderBlocked && (
                      <div className="rounded-lg bg-surface-subtle px-2.5 py-1.5 text-2xs text-content-muted">
                        Preço Final com Desconto:{' '}
                        <span className="font-bold text-brand-600 dark:text-brand-400">
                          {sim.finalPrice.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </span>{' '}
                        (Economia de{' '}
                        {sim.discount.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                        )
                      </div>
                    )}

                    {/* Aviso de Pedido Mínimo */}
                    {isMinOrderBlocked && (
                      <div className="flex items-center gap-1 text-2xs text-amber-600 dark:text-amber-400">
                        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>
                          Exige pedido mínimo de{' '}
                          {(coupon.min_order_value || 0).toLocaleString(
                            'pt-BR',
                            {
                              style: 'currency',
                              currency: 'BRL',
                            }
                          )}
                        </span>
                      </div>
                    )}

                    {/* Detalhes de marketplace e validade */}
                    <div className="flex items-center gap-2 text-2xs text-content-muted">
                      <span>
                        Marketplace: {coupon.marketplace.toUpperCase()}
                      </span>
                      {coupon.expires_at && (
                        <>
                          <span>•</span>
                          <span>
                            Válido até:{' '}
                            {new Date(coupon.expires_at).toLocaleDateString(
                              'pt-BR'
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};
